import * as Sentry from '@sentry/nextjs';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

// Import constants from the separate file
import {
  POSTHOG_DONATION_EVENT_ONETIME,
  POSTHOG_DONATION_EVENT_RECURRING,
  POSTHOG_SEND_CHAT_MESSAGE_EVENT,
  POSTHOG_SIGNIN_EMAIL_EVENT,
  POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
  POSTHOG_SIGNIN_EVENT,
  POSTHOG_SIGNUP_EVENT_FB_MESSENGER,
  POSTHOG_SIGNUP_EVENT_IMESSAGE,
  POSTHOG_SIGNUP_EVENT_INSTA,
  POSTHOG_SIGNUP_EVENT_SMS,
  POSTHOG_SIGNUP_EVENT_TELEGRAM,
  POSTHOG_SIGNUP_EVENT_WEB_CHAT,
  SIGNIN_ENTRY_MAGIC_LINK,
  SIGNIN_ENTRY_MANUAL,
} from 'utilities/analytics';

const LIFETIME_VALUE_MULTIPLIER_MONTHLY = 24;
const LIFETIME_VALUE_MULTIPLIER_YEARLY = 2;

export const useAnalytics = () => {
  const posthog = usePostHog();

  const donationTrackEvent = ({
    recurringPeriod = null,
    donationValueCents,
  }: {
    recurringPeriod?: string | null;
    donationValueCents: number;
  }) => {
    if (recurringPeriod === 'MONTH') {
      posthog?.capture(POSTHOG_DONATION_EVENT_RECURRING, {
        price: donationValueCents,
        period: 'monthly',
        value: donationValueCents * LIFETIME_VALUE_MULTIPLIER_MONTHLY,
      });
    } else if (recurringPeriod === 'YEAR') {
      posthog?.capture(POSTHOG_DONATION_EVENT_RECURRING, {
        price: donationValueCents,
        period: 'yearly',
        value: donationValueCents * LIFETIME_VALUE_MULTIPLIER_YEARLY,
      });
    } else {
      posthog?.capture(POSTHOG_DONATION_EVENT_ONETIME, {
        price: donationValueCents,
        value: donationValueCents,
      });
    }
  };

  // Memoised because callers put trackEvent in effect and useCallback
  // dependency arrays — notably the magic-link auto-login effect on the sign-in
  // page. An unstable identity there re-runs the effect on every render.
  const trackEvent = useCallback(
    ({
      eventName,
      eventData = {},
    }: {
      eventName: string;
      eventData?: Record<string, unknown>;
    }) => {
      // Report bad inputs to sentry
      if (!eventName) {
        Sentry.addBreadcrumb({
          category: 'analytics',
          message: 'trackEvent: missing eventName',
          level: 'error',
          data: { eventName, eventData },
        });
        Sentry.captureException(new Error('trackEvent: missing eventName'));
      }
      // Optional-chained deliberately: usePostHog returns undefined when the
      // client hasn't initialised (blocked script, missing key), and callers
      // include the sign-in flow, where a throw from analytics would surface to
      // the user as a failed login.
      posthog?.capture(eventName, eventData);
    },
    [posthog]
  );

  return { donationTrackEvent, trackEvent };
};

export {
  POSTHOG_DONATION_EVENT_ONETIME,
  POSTHOG_DONATION_EVENT_RECURRING,
  POSTHOG_SEND_CHAT_MESSAGE_EVENT,
  POSTHOG_SIGNIN_EMAIL_EVENT,
  POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
  POSTHOG_SIGNIN_EVENT,
  POSTHOG_SIGNUP_EVENT_FB_MESSENGER,
  POSTHOG_SIGNUP_EVENT_IMESSAGE,
  POSTHOG_SIGNUP_EVENT_INSTA,
  POSTHOG_SIGNUP_EVENT_SMS,
  POSTHOG_SIGNUP_EVENT_TELEGRAM,
  POSTHOG_SIGNUP_EVENT_WEB_CHAT,
  SIGNIN_ENTRY_MAGIC_LINK,
  SIGNIN_ENTRY_MANUAL,
};
